/**
 * SupplierPaymentModal — COM-05: Registrar Pago a Proveedor
 * Soporta creación (payment undefined) y edición (payment definido, solo si status === 'registered').
 * Con preselectedInvoice: pre-rellena proveedor, factura y saldo pendiente.
 */
import { todayISO } from '../../../lib/utils'
import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { SupplierAutocomplete } from './SupplierAutocomplete'
import DatePicker from '@/components/DatePicker'
import { NumericInput } from '@/components/NumericInput'
import { Loader2 } from 'lucide-react'
import { useSupplierInvoices, type PaymentMethod, type SupplierPayment, type PayableInvoice } from '../hooks/usePurchases'
import { BankAccountSelect } from '@/modules/accounting/components/BankAccountSelect'
import { formatMoneyForTenant, formatNumber, useTenantLocale } from '@/lib/locale'
import { inputCls } from '@/lib/formStyles'
import { Modal } from '@/components/ds/Modal'

interface Props {
  onSave: (data: Record<string, unknown>) => void
  onClose: () => void
  isPending: boolean
  error?: string | null
  payment?: SupplierPayment
  preselectedInvoice?: PayableInvoice | null
}

type FormData = {
  supplier: string
  invoice: string
  payment_date: string
  amount: string
  currency: string
  exchange_rate: string
  payment_method: PaymentMethod
  reference: string
  notes: string
  bank_account: string
  leave_pending: boolean
}

/** Monedas extranjeras que ofrece el selector de moneda del pago. */
const FX_OPTIONS = ['USD', 'EUR']

export function SupplierPaymentModal({ onSave, onClose, isPending, error, payment, preselectedInvoice }: Props) {
  const locale = useTenantLocale()
  const isEditMode = !!payment
  // Editable salvo anulado. Un pago CONFIRMADO se edita en sitio: el backend
  // actualiza su comprobante sin reversa (exige el permiso `edit_confirmed` y
  // período abierto). Antes solo se permitía 'registered', lo que hacía que el
  // form se autocerrara con un confirmado y volviera al detalle en loop.
  const canEdit = isEditMode && payment?.status !== 'cancelled'
  const isPreselected = !!preselectedInvoice
  // Factura en moneda extranjera: su saldo real vive en esa moneda. En CLP puede
  // quedar en 0 aunque falten dólares por pagar (los pagos se convierten a la
  // tasa de su fecha, no a la de la factura), así que el pago se propone en la
  // moneda de la factura y por su saldo en ella (#1485). Solo las monedas que
  // ofrece el selector.
  const invoiceCurrency = (preselectedInvoice?.currency_original || '').toUpperCase()
  const fxOutstanding = FX_OPTIONS.includes(invoiceCurrency) && preselectedInvoice?.outstanding_original != null
    ? preselectedInvoice.outstanding_original
    : null


  const { register, handleSubmit, watch, control, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      supplier: payment?.supplier || preselectedInvoice?.supplier || '',
      invoice: payment?.invoice || preselectedInvoice?.id || '',
      payment_date: payment?.payment_date || todayISO(),
      amount: payment?.amount || fxOutstanding || preselectedInvoice?.outstanding || '',
      currency: isEditMode ? (payment?.currency || '') : (fxOutstanding != null ? invoiceCurrency : ''),
      exchange_rate: payment?.exchange_rate || '',
      payment_method: payment?.payment_method || 'transfer',
      reference: payment?.reference || '',
      notes: payment?.notes || '',
      bank_account: payment?.bank_account || '',
      leave_pending: false,
    },
  })

  // Un pago anulado no se puede editar: cerrar el modal si se abrió igual.
  useEffect(() => {
    if (isEditMode && !canEdit) {
      onClose()
    }
  }, [isEditMode, canEdit, onClose])

  const selectedSupplier = watch('supplier')
  const currency = watch('currency')
  const amount = watch('amount')
  const exchangeRate = watch('exchange_rate')
  const isFx = !!currency && currency !== 'CLP'
  const clpPreview = isFx && amount && exchangeRate
    ? (parseFloat(amount) * parseFloat(exchangeRate))
    : null
  const { data: invoicesData } = useSupplierInvoices({
    supplier_id: selectedSupplier || undefined,
    status: 'approved',
    page_size: 50,
  })

  const onSubmit = (data: FormData) => {
    const payload: Record<string, unknown> = {
      supplier: data.supplier,
      invoice: data.invoice || null,
      payment_date: data.payment_date,
      amount: data.amount,
      payment_method: data.payment_method,
      reference: data.reference || '',
      notes: data.notes || '',
    }
    if (data.bank_account) payload.bank_account = data.bank_account
    // Moneda: CLP (o vacío) ⇒ sin tasa. USD/EUR ⇒ enviar moneda; la tasa es
    // opcional (el backend la resuelve por la fecha de pago si va vacía).
    payload.currency = data.currency && data.currency !== 'CLP' ? data.currency : ''
    if (payload.currency && data.exchange_rate) payload.exchange_rate = data.exchange_rate
    // Auto-confirmación: por defecto el pago queda Confirmado (ya está en banco).
    // "Dejar pendiente" lo deja Registrado para confirmar luego.
    payload.confirm = !data.leave_pending
    onSave(payload)
  }

  const lockSupplier = isEditMode || isPreselected

  return (
    <Modal open onClose={onClose} title={isEditMode ? 'Editar Pago a Proveedor' : 'Registrar Pago a Proveedor'} subtitle={isPreselected ? `Factura ${preselectedInvoice.invoice_number} · ${preselectedInvoice.supplier_name}` : undefined} size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="px-4 py-3 bg-danger-surface border border-danger-border rounded-lg text-sm text-danger-content">{error}</div>
        )}

        {preselectedInvoice?.is_non_billable && (
          <div className="px-4 py-3 bg-warning-surface border border-warning-border rounded-lg text-sm text-warning-content">
            <strong>Documento con monto no facturable.</strong> El comprobante contable de este pago quedará <strong>en borrador</strong> para que contabilidad revise la cuenta correcta (compra de divisa, cuenta transitoria o cuenta por pagar) antes de aprobarlo.
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Proveedor *</label>
          {lockSupplier ? (
            <>
              <div className={`${inputCls} bg-surface-muted text-content-muted`}>
                {preselectedInvoice?.supplier_name || payment?.supplier_name || '—'}
              </div>
              {/* Registrado y sin `disabled`: react-hook-form excluye del submit los
                  campos disabled, así que el `<select disabled>` que había acá dejaba
                  `supplier` en undefined al editar un pago y no se podía guardar.
                  Además el proveedor podía no estar en `useSuppliers` (solo activos,
                  page_size 100), dejando el select en blanco. */}
              <input type="hidden" {...register('supplier', { required: 'Requerido' })} />
            </>
          ) : (
            <>
              <Controller name="supplier" control={control} rules={{ required: 'Requerido' }}
                render={({ field }) => (
                  <SupplierAutocomplete aria-label="Seleccionar proveedor"
                    value={field.value} onChange={field.onChange} className={inputCls} />
                )} />
              {errors.supplier && <p className="text-xs text-danger-solid mt-0.5">{errors.supplier.message}</p>}
            </>
          )}
        </div>

        {isPreselected ? (
          <div>
            <label className="block text-xs font-medium text-content-muted mb-1">Factura</label>
            <div className={`${inputCls} bg-surface-muted text-content-muted`}>
              {preselectedInvoice.invoice_number}
              <span className="text-content-subtle ml-2 text-xs">
                Saldo: {fxOutstanding != null
                  ? `${invoiceCurrency} ${formatNumber(parseFloat(fxOutstanding), locale, 2)} · ${formatMoneyForTenant(preselectedInvoice.outstanding)}`
                  : formatMoneyForTenant(preselectedInvoice.outstanding)}
              </span>
            </div>
            <input type="hidden" {...register('invoice')} />
          </div>
        ) : selectedSupplier && invoicesData?.invoices.length ? (
          <div>
            <label htmlFor="supplierpaymentmodal-invoice" className="block text-xs font-medium text-content-muted mb-1">Factura (opcional)</label>
            <select id="supplierpaymentmodal-invoice" {...register('invoice')} className={inputCls}>
              <option value="">Sin factura asociada</option>
              {invoicesData.invoices.map(inv => (
                <option key={inv.id} value={inv.id}>
                  {inv.invoice_number} — {parseFloat(inv.total).toLocaleString('es-CL')}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="supplierpaymentmodal-payment-date" className="block text-xs font-medium text-content-muted mb-1">Fecha de Pago *</label>
            <Controller name="payment_date" control={control} rules={{ required: 'Requerido' }} render={({ field }) => <DatePicker id="supplierpaymentmodal-payment-date" value={field.value} onChange={field.onChange} onBlur={field.onBlur} required />} />
            {errors.payment_date && <p className="text-xs text-danger-solid mt-0.5">{errors.payment_date.message}</p>}
          </div>
          <div>
            <label htmlFor="supplierpaymentmodal-currency" className="block text-xs font-medium text-content-muted mb-1">Moneda</label>
            <select id="supplierpaymentmodal-currency" {...register('currency')} className={inputCls}>
              <option value="">CLP (peso)</option>
              <option value="USD">USD (dólar)</option>
              <option value="EUR">EUR (euro)</option>
            </select>
          </div>
          <div>
            <label htmlFor="supplierpaymentmodal-amount" className="block text-xs font-medium text-content-muted mb-1">
              Monto * {isFx && <span className="text-content-subtle">({currency})</span>}
            </label>
            <Controller name="amount" control={control} rules={{ required: 'Requerido' }}
              render={({ field }) => (
                <NumericInput id="supplierpaymentmodal-amount"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  locale={locale}
                  decimals={isFx ? 2 : 0}
                  placeholder="0"
                  min={0}
                  className={inputCls}
                />
              )}
            />
            {errors.amount && <p className="text-xs text-danger-solid mt-0.5">{errors.amount.message}</p>}
          </div>
          <div>
            <label htmlFor="supplierpaymentmodal-payment-method" className="block text-xs font-medium text-content-muted mb-1">Método de Pago</label>
            <select id="supplierpaymentmodal-payment-method" {...register('payment_method')} className={inputCls}>
              <option value="transfer">Transferencia</option>
              <option value="check">Cheque</option>
              <option value="cash">Efectivo</option>
              <option value="servipag">Servipag</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label htmlFor="supplierpaymentmodal-reference" className="block text-xs font-medium text-content-muted mb-1">Referencia</label>
            <input id="supplierpaymentmodal-reference" {...register('reference')} className={inputCls} placeholder="Nro. comprobante, cheque..." />
          </div>
        </div>

        {isFx && (
          <div className="bg-soft border border-divider rounded-lg px-3 py-3 space-y-2">
            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">
                  Tipo de cambio ({currency}→CLP)
                </label>
                <Controller name="exchange_rate" control={control}
                  render={({ field }) => (
                    <NumericInput aria-label={`Tipo de cambio (${currency}→CLP)`}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      locale={locale}
                      decimals={2}
                      placeholder="Tasa de la fecha de pago"
                      min={0}
                      className={inputCls}
                    />
                  )}
                />
              </div>
              <div className="text-right">
                <span className="block text-xs text-content-subtle">Equivalente en CLP</span>
                <span className="text-sm font-semibold text-content tabular-nums">
                  {clpPreview != null
                    ? formatMoneyForTenant(clpPreview)
                    : '—'}
                </span>
              </div>
            </div>
            <p className="text-[10px] text-content-subtle">
              Con la tasa vacía se usa la del día del pago. El comprobante
              queda en {currency} para la conciliación bancaria de la cuenta en {currency}.
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-content-muted mb-1">Cuenta Banco / Caja</label>
          <Controller name="bank_account" control={control}
            render={({ field }) => (
              <BankAccountSelect value={field.value} onChange={field.onChange} />
            )}
          />
          <p className="text-[10px] text-content-subtle mt-0.5">
            Cuenta desde la que se realiza el pago (Activo o Pasivo). Se busca por código o nombre. Sin cuenta seleccionada se usa la cuenta por defecto de Contabilidad.
          </p>
        </div>

        <div>
          <label htmlFor="supplierpaymentmodal-notes" className="block text-xs font-medium text-content-muted mb-1">Notas</label>
          <textarea id="supplierpaymentmodal-notes" {...register('notes')} rows={2} className={inputCls} placeholder="Observaciones..." />
        </div>

        {!isEditMode && (
          <label className="flex items-start gap-2 text-xs text-content cursor-pointer bg-soft border border-divider rounded-lg px-3 py-2">
            <input type="checkbox" {...register('leave_pending')} className="mt-0.5 rounded border-divider-strong" />
            <span>
              <strong>Dejar pendiente de confirmación.</strong> Por defecto el pago queda <strong>Confirmado</strong>
              {' '}y se genera el comprobante contable. Marcar solo si aún no está confirmado en el banco.
            </span>
          </label>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft">
            Cancelar
          </button>
          <button type="submit" disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover disabled:opacity-60">
            {isPending && <Loader2 size={13} className="animate-spin" />}
            {isEditMode ? 'Guardar Cambios' : 'Registrar Pago'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
